import React, { useState } from 'react';
import { 
  Plus, 
  X, 
  List, 
  ArrowDown, 
  ArrowUp, 
  AlertTriangle, 
  Award, 
  CheckCircle,
  Check
} from 'lucide-react';

/**
 * Main component for the Rank The Blank application
 * Implements STV voting simulation with local storage
 */
const RankTheBlank = () => {
  // Define the color palette to match the website's style
  const colorPalette = {
    primary: '#922c40',   // Burgundy/wine red
    secondary: '#DC9750', // Bronze/gold
    tertiary: '#F3EAC0',  // Cream/beige
    light: '#F9F5E3',     // Lighter cream for backgrounds
    dark: '#1E2640',      // Navy/dark blue
    white: '#FFFFFF',
    black: '#333333',
    gray: '#666666',
    lightGray: '#E5E7EB',
    success: '#10B981',   // Green
    error: '#EF4444',     // Red
    warning: '#F59E0B'    // Amber
  };
  
  // Initial state
  const [view, setView] = useState('create'); // create, vote, results
  const [error, setError] = useState(null);
  const [message, setMessage] = useState(null);
  const [loading, setLoading] = useState(false);
  
  // Create poll
  const [pollTitle, setPollTitle] = useState('');
  const [pollDescription, setPollDescription] = useState('');
  const [category, setCategory] = useState('');
  const [options, setOptions] = useState([]);
  const [newOption, setNewOption] = useState('');
  const [seatsToFill, setSeatsToFill] = useState(1);
  
  // Vote state
  const [rankings, setRankings] = useState([]);
  const [unrankedOptions, setUnrankedOptions] = useState([]);
  
  // Results
  const [results, setResults] = useState(null);
  const [rounds, setRounds] = useState([]);
  const [winners, setWinners] = useState([]);
  
  // Poll data
  const [currentPoll, setCurrentPoll] = useState(null);
  
  // Simplified handle change
  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (name === 'pollTitle') setPollTitle(value);
    else if (name === 'pollDescription') setPollDescription(value);
    else if (name === 'category') setCategory(value);
    else if (name === 'newOption') setNewOption(value);
    else if (name === 'seatsToFill') setSeatsToFill(parseInt(value) || 1);
  };

  // Add a new option to the poll
  const handleAddOption = () => {
    // Validate
    if (!newOption.trim()) {
      setError('Option cannot be empty');
      return;
    }
    
    // Check for duplicates
    if (options.some(option => option.toLowerCase() === newOption.toLowerCase())) {
      setError('This option already exists');
      return;
    }
    
    // Add option and reset input
    setOptions([...options, newOption]);
    setNewOption('');
    setError(null);
  };
  
  // Remove an option from the poll
  const handleRemoveOption = (index) => {
    const newOptions = [...options];
    newOptions.splice(index, 1);
    setOptions(newOptions);
  };
  
  // Move an option up in the rankings
  const handleMoveUp = (index) => {
    if (index === 0) return;
    
    const newRankings = [...rankings];
    const temp = newRankings[index];
    newRankings[index] = newRankings[index - 1];
    newRankings[index - 1] = temp;
    
    setRankings(newRankings);
  };
  
  // Move an option down in the rankings
  const handleMoveDown = (index) => {
    if (index === rankings.length - 1) return;
    
    const newRankings = [...rankings];
    const temp = newRankings[index];
    newRankings[index] = newRankings[index + 1];
    newRankings[index + 1] = temp;
    
    setRankings(newRankings);
  };
  
  // Add an option to rankings
  const handleAddToRankings = (option) => {
    setRankings([...rankings, option]);
    setUnrankedOptions(unrankedOptions.filter(o => o !== option));
  };
  
  // Remove an option from rankings
  const handleRemoveFromRankings = (index) => {
    const option = rankings[index];
    const newRankings = [...rankings];
    newRankings.splice(index, 1);
    
    setRankings(newRankings);
    setUnrankedOptions([...unrankedOptions, option]);
  };
  
  // Create a new poll (simplified, stores in local state only)
  const handleCreatePoll = (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);
    
    try {
      // Validate inputs
      if (!pollTitle) {
        throw new Error('Please enter a title for your poll');
      }
      
      if (options.length < 2) {
        throw new Error('Please add at least 2 options to rank');
      }
      
      // Create poll
      const poll = {
        title: pollTitle,
        description: pollDescription,
        category: category || 'Uncategorized',
        options: options,
        seatsToFill: parseInt(seatsToFill, 10) || 1,
        createdAt: new Date().toLocaleString(),
        votes: []
      };
      
      // In a real app, we would save to Firebase here
      // For now, just store in component state
      setCurrentPoll(poll);
      setUnrankedOptions([...options]);
      setRankings([]);
      
      // Go to vote view
      setView('vote');
      setMessage('Poll created successfully!');
      setLoading(false);
      
    } catch (error) {
      setError(error.message || 'Failed to create poll. Please try again.');
      setLoading(false);
    }
  };
  
  // Submit a vote (simplified, stores in local state only)
  const handleVote = () => {
    // Validate
    if (rankings.length === 0) {
      setError('Please rank at least one option');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Create vote object
      const vote = {
        rankings,
        timestamp: new Date().toLocaleString()
      };
      
      // Add vote to poll
      const updatedPoll = {
        ...currentPoll,
        votes: [...currentPoll.votes, vote]
      };
      
      setCurrentPoll(updatedPoll);
      
      // Calculate results
      const results = calculateResults(updatedPoll);
      
      setView('results');
      setMessage('Your vote has been recorded!');
      setLoading(false);
      
    } catch (error) {
      setError(error.message || 'Failed to submit vote. Please try again.');
      setLoading(false);
    }
  };
  
  // Calculate STV voting results
  const calculateResults = (poll) => {
    const { votes, options, seatsToFill } = poll;
    
    // Extract all ballots from votes
    const ballots = votes.map(vote => vote.rankings);
    
    // Initialize tracking variables
    const eliminated = [];
    const elected = [];
    const calculatedRounds = [];
    
    // Calculate Droop quota (minimum votes needed to secure a seat)
    const numVotes = ballots.length;
    const droopQuota = Math.floor(numVotes / (seatsToFill + 1)) + 1;
    
    // Continue counting rounds until all seats are filled
    let round = 1;
    
    while (elected.length < seatsToFill && eliminated.length < options.length - seatsToFill) {
      // Count first preferences 
      const voteCount = {};
      options.forEach(option => {
        voteCount[option] = 0;
      });
      
      // Count current round of votes
      ballots.forEach(ballot => {
        // Find the first preference that hasn't been elected or eliminated
        for (const option of ballot) {
          if (!eliminated.includes(option) && !elected.includes(option)) {
            voteCount[option] += 1;
            break;
          }
        }
      });
      
      // Determine candidates who reached the quota
      const electedThisRound = [];
      Object.entries(voteCount).forEach(([option, votes]) => {
        if (votes >= droopQuota && !elected.includes(option)) {
          electedThisRound.push(option);
          elected.push(option);
        }
      });
      
      // Record round results
      calculatedRounds.push({
        round,
        voteCount: { ...voteCount },
        elected: [...electedThisRound],
        eliminated: null
      });
      
      // If no candidates were elected this round, eliminate the lowest performer
      if (electedThisRound.length === 0) {
        let minVotes = Number.MAX_VALUE;
        let optionToEliminate = null;
        
        Object.entries(voteCount).forEach(([option, votes]) => {
          if (
            votes > 0 && 
            votes < minVotes && 
            !elected.includes(option) && 
            !eliminated.includes(option)
          ) {
            minVotes = votes;
            optionToEliminate = option;
          }
        });
        
        if (optionToEliminate) {
          eliminated.push(optionToEliminate);
          
          // Update round info to include eliminated candidate
          calculatedRounds[calculatedRounds.length - 1].eliminated = optionToEliminate;
        } else {
          // Can't eliminate anyone else, exit the loop
          break;
        }
      }
      
      round++;
    }
    
    // If we still have seats to fill, elect the remaining candidates with the most votes
    if (elected.length < seatsToFill) {
      const lastRound = calculatedRounds[calculatedRounds.length - 1];
      const remainingCandidates = Object.entries(lastRound.voteCount)
        .filter(([option, _]) => !elected.includes(option) && !eliminated.includes(option))
        .sort(([_, votesA], [__, votesB]) => votesB - votesA);
      
      for (let i = 0; i < Math.min(remainingCandidates.length, seatsToFill - elected.length); i++) {
        elected.push(remainingCandidates[i][0]);
      }
    }
    
    // Save results to state
    const calculatedResults = {
      elected,
      eliminated,
      droopQuota,
      totalVotes: numVotes
    };
    
    setResults(calculatedResults);
    setRounds(calculatedRounds);
    setWinners(elected);
    
    return {
      elected,
      eliminated,
      rounds: calculatedRounds,
      droopQuota,
      totalVotes: numVotes
    };
  };
  
  // Reset and start over
  const handleStartOver = () => {
    setPollTitle('');
    setPollDescription('');
    setCategory('');
    setOptions([]);
    setNewOption('');
    setSeatsToFill(1);
    setRankings([]);
    setUnrankedOptions([]);
    setResults(null);
    setRounds([]);
    setWinners([]);
    setCurrentPoll(null);
    setView('create');
    setError(null);
    setMessage(null);
  };
  
  // Render create poll view
  const renderCreate = () => {
    return (
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: colorPalette.white,
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            color: colorPalette.primary,
            marginBottom: '20px',
            textAlign: 'center',
            fontSize: '1.8rem'
          }}>
            Create a New Ranking Poll
          </h2>
          
          {/* Error message */}
          {error && (
            <div style={{
              backgroundColor: `${colorPalette.error}20`,
              color: colorPalette.error,
              padding: '10px 15px',
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '0.9rem'
            }}>
              <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              {error}
            </div>
          )}
          
          {/* Create poll form */}
          <form onSubmit={handleCreatePoll}>
            <div style={{ marginBottom: '20px' }}>
              <label 
                htmlFor="pollTitle"
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                What are you ranking?
              </label>
              <input
                type="text"
                id="pollTitle"
                name="pollTitle"
                value={pollTitle}
                onChange={handleChange}
                placeholder="e.g., Best Pizza Toppings, Favorite Movies, etc."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${colorPalette.lightGray}`,
                  borderRadius: '4px',
                  fontSize: '1rem',
                  outline: 'none'
                }}
                required
              />
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label 
                htmlFor="pollDescription"
                style={{
                  display: 'block',
                  marginBottom: '5px',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                Description (optional)
              </label>
              <textarea
                id="pollDescription"
                name="pollDescription"
                value={pollDescription}
                onChange={handleChange}
                placeholder="Add some details about your poll..."
                style={{
                  width: '100%',
                  padding: '12px',
                  border: `1px solid ${colorPalette.lightGray}`,
                  borderRadius: '4px',
                  fontSize: '1rem',
                  minHeight: '80px',
                  resize: 'vertical',
                  outline: 'none'
                }}
              />
            </div>
            
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
              gap: '20px',
              marginBottom: '20px'
            }}>
              <div>
                <label 
                  htmlFor="category"
                  style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  Category
                </label>
                <select
                  id="category"
                  name="category"
                  value={category}
                  onChange={handleChange}
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colorPalette.lightGray}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    outline: 'none',
                    backgroundColor: 'white'
                  }}
                >
                  <option value="">Select a category</option>
                  <option value="Food">Food</option>
                  <option value="Movies">Movies</option>
                  <option value="Music">Music</option>
                  <option value="Sports">Sports</option>
                  <option value="Politics">Politics</option>
                  <option value="Entertainment">Entertainment</option>
                  <option value="Other">Other</option>
                </select>
              </div>
              
              <div>
                <label 
                  htmlFor="seatsToFill"
                  style={{
                    display: 'block',
                    marginBottom: '5px',
                    fontSize: '1rem',
                    fontWeight: '600'
                  }}
                >
                  Number of Winners
                </label>
                <input
                  type="number"
                  id="seatsToFill"
                  name="seatsToFill"
                  value={seatsToFill}
                  onChange={handleChange}
                  min="1"
                  max="10"
                  style={{
                    width: '100%',
                    padding: '12px',
                    border: `1px solid ${colorPalette.lightGray}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                />
                <p style={{ fontSize: '0.8rem', color: colorPalette.gray, marginTop: '5px' }}>
                  How many top choices should be selected
                </p>
              </div>
            </div>
            
            <div style={{ marginBottom: '20px' }}>
              <label 
                style={{
                  display: 'block',
                  marginBottom: '10px',
                  fontSize: '1rem',
                  fontWeight: '600'
                }}
              >
                Add Options to Rank
              </label>
              
              <div style={{
                display: 'flex',
                gap: '10px',
                marginBottom: '15px'
              }}>
                <input
                  type="text"
                  name="newOption"
                  value={newOption}
                  onChange={handleChange}
                  placeholder="Enter an option to rank"
                  style={{
                    flex: 1,
                    padding: '12px',
                    border: `1px solid ${colorPalette.lightGray}`,
                    borderRadius: '4px',
                    fontSize: '1rem',
                    outline: 'none'
                  }}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddOption();
                    }
                  }}
                />
                
                <button
                  type="button"
                  onClick={handleAddOption}
                  style={{
                    backgroundColor: colorPalette.secondary,
                    color: colorPalette.white,
                    border: 'none',
                    borderRadius: '4px',
                    padding: '12px 16px',
                    fontSize: '1rem',
                    fontWeight: '600',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}
                >
                  <Plus size={18} />
                  Add
                </button>
              </div>
              
              {/* Options list */}
              {options.length > 0 ? (
                <div style={{
                  backgroundColor: colorPalette.light,
                  borderRadius: '8px',
                  padding: '15px',
                  marginBottom: '15px'
                }}>
                  <p style={{ 
                    fontWeight: '600', 
                    marginBottom: '10px',
                    color: colorPalette.dark
                  }}>
                    Options to rank ({options.length}):
                  </p>
                  
                  <ul style={{ padding: 0, margin: 0, listStyle: 'none' }}>
                    {options.map((option, index) => (
                      <li
                        key={index}
                        style={{
                          display: 'flex',
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          padding: '8px 12px',
                          backgroundColor: 'white',
                          marginBottom: '8px',
                          borderRadius: '4px',
                          boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)'
                        }}
                      >
                        <span>{option}</span>
                        <button
                          type="button"
                          onClick={() => handleRemoveOption(index)}
                          style={{
                            backgroundColor: 'transparent',
                            color: colorPalette.error,
                            border: 'none',
                            cursor: 'pointer',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '4px'
                          }}
                        >
                          <X size={16} />
                        </button>
                      </li>
                    ))}
                  </ul>
                </div>
              ) : (
                <p style={{ 
                  color: colorPalette.gray, 
                  fontStyle: 'italic', 
                  marginBottom: '15px'
                }}>
                  No options added yet. Add at least 2 options to create your poll.
                </p>
              )}
            </div>
            
            <button
              type="submit"
              disabled={loading || options.length < 2}
              style={{
                backgroundColor: colorPalette.primary,
                color: colorPalette.white,
                border: 'none',
                borderRadius: '4px',
                padding: '12px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: (loading || options.length < 2) ? 'not-allowed' : 'pointer',
                opacity: (loading || options.length < 2) ? 0.7 : 1,
                width: '100%'
              }}
            >
              {loading ? 'Creating Poll...' : 'Create Poll'}
            </button>
          </form>
        </div>
      </div>
    );
  };
  
  // Render vote view
  const renderVote = () => {
    if (!currentPoll) {
      return (
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            backgroundColor: colorPalette.white,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              color: colorPalette.primary,
              marginBottom: '20px'
            }}>
              No Poll Found
            </h2>
            
            {error && (
              <div style={{
                backgroundColor: `${colorPalette.error}20`,
                color: colorPalette.error,
                padding: '15px',
                borderRadius: '4px',
                marginBottom: '20px',
                textAlign: 'left'
              }}>
                <AlertTriangle size={18} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
                {error}
              </div>
            )}
            
            <p>
              <button
                onClick={handleStartOver}
                style={{
                  backgroundColor: colorPalette.primary,
                  color: colorPalette.white,
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Create a New Poll
              </button>
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: colorPalette.white,
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            color: colorPalette.primary,
            marginBottom: '5px',
            textAlign: 'center',
            fontSize: '1.8rem'
          }}>
            {currentPoll.title}
          </h2>
          
          {currentPoll.description && (
            <p style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              color: colorPalette.gray
            }}>
              {currentPoll.description}
            </p>
          )}
          
          <div style={{
            backgroundColor: colorPalette.tertiary,
            padding: '15px',
            borderRadius: '8px',
            marginBottom: '20px',
            textAlign: 'center'
          }}>
            <p style={{ margin: 0, fontSize: '0.95rem' }}>
              <strong>How to vote:</strong> Rank your choices in order of preference. Your vote will help elect {currentPoll.seatsToFill} winner{currentPoll.seatsToFill > 1 ? 's' : ''}.
            </p>
          </div>
          
          {/* Error message */}
          {error && (
            <div style={{
              backgroundColor: `${colorPalette.error}20`,
              color: colorPalette.error,
              padding: '10px 15px',
              borderRadius: '4px',
              marginBottom: '20px',
              fontSize: '0.9rem'
            }}>
              <AlertTriangle size={16} style={{ verticalAlign: 'middle', marginRight: '8px' }} />
              {error}
            </div>
          )}
          
          <div style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
            gap: '20px'
          }}>
            {/* Ranked choices */}
            <div>
              <h3 style={{
                fontSize: '1.2rem',
                marginBottom: '15px',
                color: colorPalette.dark,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <List size={18} />
                Your Ranked Choices
              </h3>
              
              {rankings.length === 0 ? (
                <div style={{
                  backgroundColor: colorPalette.light,
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: colorPalette.gray,
                  fontStyle: 'italic'
                }}>
                  You haven't ranked any choices yet. Click on options from the list on the right.
                </div>
              ) : (
                <div>
                  {rankings.map((option, index) => (
                    <div
                      key={`ranking-${index}`}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        backgroundColor: 'white',
                        border: `1px solid ${colorPalette.lightGray}`,
                        borderRadius: '4px',
                        padding: '10px 15px',
                        marginBottom: '10px',
                        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.05)'
                      }}
                    >
                      <div style={{
                        backgroundColor: colorPalette.primary,
                        color: 'white',
                        width: '28px',
                        height: '28px',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        fontWeight: 'bold',
                        marginRight: '12px',
                        flexShrink: 0
                      }}>
                        {index + 1}
                      </div>
                      
                      <span style={{ flex: 1 }}>{option}</span>
                      
                      <div style={{ display: 'flex', gap: '5px' }}>
                        <button
                          type="button"
                          onClick={() => handleMoveUp(index)}
                          disabled={index === 0}
                          style={{
                            backgroundColor: index === 0 ? '#f1f1f1' : colorPalette.light,
                            border: 'none',
                            borderRadius: '4px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: index === 0 ? 'not-allowed' : 'pointer',
                            color: index === 0 ? '#bbb' : colorPalette.dark
                          }}
                        >
                          <ArrowUp size={16} />
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleMoveDown(index)}
                          disabled={index === rankings.length - 1}
                          style={{
                            backgroundColor: index === rankings.length - 1 ? '#f1f1f1' : colorPalette.light,
                            border: 'none',
                            borderRadius: '4px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: index === rankings.length - 1 ? 'not-allowed' : 'pointer',
                            color: index === rankings.length - 1 ? '#bbb' : colorPalette.dark
                          }}
                        >
                          <ArrowDown size={16} />
                        </button>
                        
                        <button
                          type="button"
                          onClick={() => handleRemoveFromRankings(index)}
                          style={{
                            backgroundColor: `${colorPalette.error}20`,
                            border: 'none',
                            borderRadius: '4px',
                            width: '28px',
                            height: '28px',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            cursor: 'pointer',
                            color: colorPalette.error
                          }}
                        >
                          <X size={16} />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Unranked choices */}
            <div>
              <h3 style={{
                fontSize: '1.2rem',
                marginBottom: '15px',
                color: colorPalette.dark,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}>
                <Check size={18} />
                Available Options
              </h3>
              
              {unrankedOptions.length === 0 ? (
                <div style={{
                  backgroundColor: colorPalette.light,
                  padding: '20px',
                  borderRadius: '8px',
                  textAlign: 'center',
                  color: colorPalette.gray,
                  fontStyle: 'italic'
                }}>
                  All options have been ranked. You can remove options from your ranking if needed.
                </div>
              ) : (
                <div style={{ 
                  backgroundColor: colorPalette.light,
                  padding: '15px',
                  borderRadius: '8px'
                }}>
                  {unrankedOptions.map((option, index) => (
                    <button
                      key={`unranked-${index}`}
                      onClick={() => handleAddToRankings(option)}
                      style={{
                        display: 'block',
                        width: '100%',
                        textAlign: 'left',
                        backgroundColor: 'white',
                        border: `1px solid ${colorPalette.lightGray}`,
                        borderRadius: '4px',
                        padding: '10px 15px',
                        marginBottom: '8px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        fontSize: '1rem'
                      }}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>
          
          <div style={{ marginTop: '30px', display: 'flex', justifyContent: 'center' }}>
            <button
              onClick={handleVote}
              disabled={rankings.length === 0 || loading}
              style={{
                backgroundColor: colorPalette.primary,
                color: colorPalette.white,
                border: 'none',
                borderRadius: '4px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: rankings.length === 0 || loading ? 'not-allowed' : 'pointer',
                opacity: rankings.length === 0 || loading ? 0.7 : 1,
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              {loading ? 'Submitting...' : 'Submit Your Vote'}
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Render results view
  const renderResults = () => {
    if (!currentPoll || !results) {
      return (
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '20px',
          textAlign: 'center'
        }}>
          <div style={{
            backgroundColor: colorPalette.white,
            borderRadius: '8px',
            padding: '20px',
            boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
          }}>
            <h2 style={{
              color: colorPalette.primary,
              marginBottom: '20px'
            }}>
              No Results Available
            </h2>
            
            <p>
              <button
                onClick={handleStartOver}
                style={{
                  backgroundColor: colorPalette.primary,
                  color: colorPalette.white,
                  border: 'none',
                  borderRadius: '4px',
                  padding: '10px 20px',
                  fontSize: '1rem',
                  fontWeight: '600',
                  cursor: 'pointer',
                  marginTop: '10px'
                }}
              >
                Create a New Poll
              </button>
            </p>
          </div>
        </div>
      );
    }
    
    return (
      <div style={{
        maxWidth: '800px',
        margin: '0 auto',
        padding: '20px'
      }}>
        <div style={{
          backgroundColor: colorPalette.white,
          borderRadius: '8px',
          padding: '20px',
          boxShadow: '0 4px 15px rgba(0, 0, 0, 0.1)'
        }}>
          <h2 style={{
            color: colorPalette.primary,
            marginBottom: '5px',
            textAlign: 'center',
            fontSize: '1.8rem'
          }}>
            Results: {currentPoll.title}
          </h2>
          
          {currentPoll.description && (
            <p style={{ 
              textAlign: 'center', 
              marginBottom: '20px',
              color: colorPalette.gray
            }}>
              {currentPoll.description}
            </p>
          )}
          
          {/* Success message */}
          {message && (
            <div style={{
              backgroundColor: `${colorPalette.success}20`,
              color: colorPalette.success,
              padding: '12px 15px',
              borderRadius: '4px',
              marginBottom: '20px',
              textAlign: 'center',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}>
              <CheckCircle size={18} />
              {message}
            </div>
          )}
          
          {/* Winners section */}
          <div style={{
            backgroundColor: colorPalette.tertiary,
            borderRadius: '8px',
            padding: '20px',
            marginBottom: '30px',
            textAlign: 'center'
          }}>
            <h3 style={{
              color: colorPalette.dark,
              marginBottom: '15px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              fontSize: '1.3rem'
            }}>
              <Award size={24} style={{ color: colorPalette.secondary }} />
              Winners
            </h3>
            
            <div style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: '10px',
              justifyContent: 'center'
            }}>
              {winners.map((winner, index) => (
                <div
                  key={`winner-${index}`}
                  style={{
                    backgroundColor: 'white',
                    borderRadius: '8px',
                    padding: '15px',
                    boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)',
                    minWidth: '150px',
                    textAlign: 'center'
                  }}
                >
                  <div style={{
                    backgroundColor: colorPalette.primary,
                    color: 'white',
                    width: '32px',
                    height: '32px',
                    borderRadius: '50%',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 'bold',
                    margin: '0 auto 10px'
                  }}>
                    {index + 1}
                  </div>
                  
                  <div style={{
                    fontSize: '1.1rem',
                    fontWeight: '600',
                    color: colorPalette.dark
                  }}>
                    {winner}
                  </div>
                </div>
              ))}
            </div>
            
            <p style={{ 
              marginTop: '15px', 
              color: colorPalette.gray, 
              fontSize: '0.9rem' 
            }}>
              Droop quota: {results.droopQuota} votes needed to win a seat
              <br />
              Total votes: {results.totalVotes}
            </p>
          </div>
          
          {/* Rounds of counting */}
          <h3 style={{
            fontSize: '1.2rem',
            marginBottom: '15px',
            color: colorPalette.dark
          }}>
            Counting Process
          </h3>
          
          {rounds.map((round, roundIndex) => (
            <div
              key={`round-${roundIndex}`}
              style={{
                backgroundColor: colorPalette.light,
                borderRadius: '8px',
                padding: '15px',
                marginBottom: '15px'
              }}
            >
              <h4 style={{
                fontSize: '1.1rem',
                color: colorPalette.dark,
                marginBottom: '10px'
              }}>
                Round {round.round}
              </h4>
              
              <div style={{ marginBottom: '15px' }}>
                <table style={{
                  width: '100%',
                  borderCollapse: 'collapse',
                  fontSize: '0.9rem'
                }}>
                  <thead>
                    <tr>
                      <th style={{
                        textAlign: 'left',
                        padding: '8px',
                        borderBottom: `1px solid ${colorPalette.lightGray}`
                      }}>
                        Option
                      </th>
                      <th style={{
                        textAlign: 'right',
                        padding: '8px',
                        borderBottom: `1px solid ${colorPalette.lightGray}`
                      }}>
                        Votes
                      </th>
                      <th style={{
                        textAlign: 'right',
                        padding: '8px',
                        borderBottom: `1px solid ${colorPalette.lightGray}`
                      }}>
                        Status
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.entries(round.voteCount)
                      .filter(([option, votes]) => votes > 0 || winners.includes(option))
                      .sort(([_, votesA], [__, votesB]) => votesB - votesA)
                      .map(([option, votes], index) => (
                        <tr key={`vote-${roundIndex}-${index}`}>
                          <td style={{
                            padding: '8px',
                            borderBottom: `1px solid ${colorPalette.lightGray}`
                          }}>
                            {option}
                          </td>
                          <td style={{
                            textAlign: 'right',
                            padding: '8px',
                            borderBottom: `1px solid ${colorPalette.lightGray}`
                          }}>
                            {votes}
                          </td>
                          <td style={{
                            textAlign: 'right',
                            padding: '8px',
                            borderBottom: `1px solid ${colorPalette.lightGray}`,
                            color: round.elected.includes(option) 
                              ? colorPalette.success
                              : option === round.eliminated
                                ? colorPalette.error
                                : colorPalette.gray
                          }}>
                            {round.elected.includes(option) 
                              ? 'Elected'
                              : option === round.eliminated
                                ? 'Eliminated'
                                : ''}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
              
              <div style={{
                backgroundColor: 'white',
                padding: '12px',
                borderRadius: '4px',
                fontSize: '0.9rem'
              }}>
                {round.elected.length > 0 ? (
                  <p style={{
                    margin: 0,
                    color: colorPalette.success,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <CheckCircle size={16} />
                    <strong>{round.elected.join(', ')}</strong> reached the quota and {round.elected.length === 1 ? 'was' : 'were'} elected.
                  </p>
                ) : round.eliminated ? (
                  <p style={{
                    margin: 0,
                    color: colorPalette.error,
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px'
                  }}>
                    <X size={16} />
                    <strong>{round.eliminated}</strong> had the fewest votes and was eliminated.
                  </p>
                ) : (
                  <p style={{ margin: 0, color: colorPalette.gray }}>
                    No candidates were elected or eliminated in this round.
                  </p>
                )}
              </div>
            </div>
          ))}
          
          <div style={{ display: 'flex', justifyContent: 'center', marginTop: '30px' }}>
            <button
              onClick={handleStartOver}
              style={{
                backgroundColor: colorPalette.secondary,
                color: colorPalette.white,
                border: 'none',
                borderRadius: '4px',
                padding: '12px 24px',
                fontSize: '1rem',
                fontWeight: '600',
                cursor: 'pointer'
              }}
            >
              Create a New Poll
            </button>
          </div>
        </div>
      </div>
    );
  };
  
  // Main render function
  return (
    <div style={{ 
      fontFamily: 'system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      backgroundColor: '#f7f8fa',
      minHeight: '100vh',
      color: colorPalette.dark
    }}>
      <header style={{
        backgroundColor: colorPalette.primary,
        color: 'white',
        padding: '20px 0',
        textAlign: 'center',
        boxShadow: '0 2px 10px rgba(0, 0, 0, 0.1)'
      }}>
        <div style={{
          maxWidth: '800px',
          margin: '0 auto',
          padding: '0 20px'
        }}>
          <h1 style={{ margin: '0 0 10px 0', fontSize: '2.2rem' }}>
            Rank The Blank
          </h1>
          <p style={{ margin: 0, opacity: 0.9 }}>
            Create and vote on ranked-choice polls using Single Transferable Vote (STV)
          </p>
        </div>
      </header>
      
      {/* View switcher */}
      <nav style={{
        maxWidth: '800px',
        margin: '20px auto',
        padding: '0 20px',
        display: 'flex',
        justifyContent: 'center',
        gap: '10px'
      }}>
        <button
          onClick={() => setView('create')}
          style={{
            backgroundColor: view === 'create' ? colorPalette.primary : colorPalette.light,
            color: view === 'create' ? colorPalette.white : colorPalette.dark,
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: 'pointer'
          }}
        >
          Create Poll
        </button>
        
        <button
          onClick={() => setView('vote')}
          disabled={!currentPoll}
          style={{
            backgroundColor: view === 'vote' ? colorPalette.primary : colorPalette.light,
            color: view === 'vote' ? colorPalette.white : colorPalette.dark,
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: !currentPoll ? 'not-allowed' : 'pointer',
            opacity: !currentPoll ? 0.7 : 1
          }}
        >
          Vote
        </button>
        
        <button
          onClick={() => setView('results')}
          disabled={!results}
          style={{
            backgroundColor: view === 'results' ? colorPalette.primary : colorPalette.light,
            color: view === 'results' ? colorPalette.white : colorPalette.dark,
            border: 'none',
            borderRadius: '4px',
            padding: '10px 15px',
            fontSize: '0.9rem',
            fontWeight: '600',
            cursor: !results ? 'not-allowed' : 'pointer',
            opacity: !results ? 0.7 : 1
          }}
        >
          Results
        </button>
      </nav>
      
      {/* Main content */}
      {view === 'create' && renderCreate()}
      {view === 'vote' && renderVote()}
      {view === 'results' && renderResults()}
      
      {/* Footer */}
      <footer style={{
        maxWidth: '800px',
        margin: '40px auto 20px',
        padding: '20px',
        textAlign: 'center',
        color: colorPalette.gray,
        fontSize: '0.9rem'
      }}>
        <p>
          Rank The Blank: A simple STV voting tool
        </p>
        <p style={{ fontSize: '0.8rem' }}>
          &copy; {new Date().getFullYear()} Rank The Blank
        </p>
      </footer>
    </div>
  );
};

export default RankTheBlank;